import React from 'react'
import {CategoryColumnsItem} from './types'
import {Button} from '@/components/cta'
import {isPreviewing, isEditing} from '@builder.io/sdk-react'
import {useFetchBuilderReference} from '@/builder/hooks/useFetchBuilderReference'

const ColumnItem: React.FC<CategoryColumnsItem> = ({
    isReferenceItem,
    referenceID,
    model,
    columnImage,
    external,
    external_link,
    link,
    subtitle,
    text,
    ctaText
}) => {
    const urlLink = external
        ? external_link?.value?.data?.url ?? '#'
        : link?.value?.data?.url ?? '#'

    const isBuilderPreview = isPreviewing() || isEditing()
    const shouldQueryReference = isBuilderPreview && isReferenceItem && !!model && !!referenceID
    const {data: fetchedData, error} = useFetchBuilderReference({
        model: model,
        shouldQuery: shouldQueryReference,
        shouldEnrich: true,
        referenceId: referenceID,
        fields: undefined
    })

    /*
    -----------------------------------------------------------------------------

     for some reason reference data passed in through props  isnt available in preview mode -_-
    // resrouces: https://forum.builder.io/t/builder-not-passing-reference-value-while-in-the-visual-editor/3796
    // https://forum.builder.io/t/couldnt-get-the-reference-data-from-the-parent-data/3476/2


    Data Fetching and Prop Resolution for Category Columns

    - Resolves the correct link (urlLink) based on whether the item is external or not.
    - Detects if the component is in Builder.io preview/edit mode and, if so, fetches reference data using useFetchBuilderReference.
    - Only uses fetched reference data if in preview/edit mode, the reference is valid, and there is no error.
    - For each display field (image, subtitle, text, CTA text), uses the value from reference data if available, otherwise falls back to the direct prop.
    - Determines if the item is external from reference data or the original prop.
    - Sets displayUrlLink based on whether the item is external:
        - If external, uses the referenced category link's URL.
        - Otherwise, uses the referenced external link's URL if available.
        - Falls back to the original urlLink.
        
    -----------------------------------------------------------------------------
    */

    const useFetched = shouldQueryReference && !error && fetchedData
    const refData = useFetched && fetchedData?.data ? fetchedData.data : {}
    const displayImage = refData.imageUpload || columnImage
    const displaySubtitle = refData.subtitle || subtitle
    const displayText = refData.text || text
    const displayCtaText = refData.cta || ctaText
    const isExternal = refData.external ?? external
    let displayUrlLink = urlLink
    if (isExternal) {
        displayUrlLink = refData.categoryLink?.value?.data?.url || urlLink
    } else if (refData.externalLink?.value?.data?.url) {
        displayUrlLink = refData.externalLink?.value?.data?.url || urlLink
    }

    return (
        <div className="flex flex-col items-center lg:items-start p-4 justify-start rounded-lg bg-white max-h-[524px] max-w-[396px] gap-y-[20px] lg:gap-y-[24px]">
            <picture>
                {/* Add <source> tags here for responsive images if available */}
                <img
                    src={displayImage}
                    alt={displaySubtitle || 'Column image'}
                    className="w-full max-w-[396px] rounded-md object-cover max-h-[346px]"
                />
            </picture>
            <div className="flex flex-col gap-y-2 lg:gap-y-4 w-full">
                <div className="text-center lg:text-left text-[20px] lg:text-[24px]">
                    {displaySubtitle}
                </div>
                <div className="text-[#142745] text-center lg:text-left">{displayText}</div>
                <Button
                    clickFunction="link"
                    variant="navLink"
                    url={displayUrlLink}
                    className="text-center lg:text-left hover:bg-transparent hover:text-[#142745] text-[#142745] text-[20px] underline font-grotesque pl-[0px]"
                >
                    {displayCtaText}
                </Button>
            </div>
        </div>
    )
}

export default ColumnItem
